import * as _ from "lodash";
import moment from "moment";
import { ComponentProps, FC, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import {
  Typography,
  ImpactTracking,
  Col,
  Row,
  Skeleton,
  Alert,
  Space,
  Tag,
  Button,
  PlusOutlined,
  Spin
} from "@ctra/components";

import { useTranslation } from "@ctra/i18n";
import { Enterprise as Content } from "@ctra/i18n";
import { DataAvailability, Direction, ImpactTracking as ImpactTrackingAPI, Result } from "@ctra/api";
import { UnitSystem } from "@ctra/utils";
import { useGoogleAnalytics } from "@ctra/analytics";

import { useLocalization, DataDictionaryContext } from "@base";
import { ImpactTrackingContext, useEvent, GAActions } from "@events";
import { ChartPicker, LayoutProvider } from "@analytics";
import { useFarm } from "@farms";

import { OverlapEvent } from "../OverlapEvent";
import styles from "./ImpactTrackedEventPage.module.less";

const { Title, Paragraph, Text } = Typography;
const { KPIChange, DateRangePicker, Variant, WidgetWrapper } = ImpactTracking;

/**
 * Impact tracked event page component
 * @returns {JSX.Element}
 * @constructor
 */
export const ImpactTrackedEventPage: FC = () => {
  const { t } = useTranslation();
  const { unitSystem } = useLocalization();
  const [currentTimeSpan, setCurrentTimeSpan] = useState("P3D");
  const [newMetric, setNewMetric] = useState("");
  const dispatch = useDispatch();
  const { farm } = useFarm();
  const isKPIProcessing = useRef(false);
  const { trackEvent } = useGoogleAnalytics();

  const {
    events: {
      page: { title, description }
    },
    unitSystem: { unit },
    impactTracking: { timePeriod, noData, whatElse, beta, add },
    kpi,
    askIda: {
      answer: {
        change: { after, before }
      },
      createTracker: {
        configure: {
          labels: { change },
          placeholders: { kpi: kpiCol }
        }
      },
      noData: { description: unsupportedIntervalDescription }
    }
  } = Content;

  const {
    event: {
      id,
      source: { type },
      startAt
    }
  } = useEvent();

  /**
   * Durations for date range
   * @type {string[]}
   */
  const currentDurations = ["P3D", "P1W", "P2W", "P4W"];

  /**
   * Get the value/label for the given result set
   * @param {Result} input
   * @param {"before" | "after"} variant
   * @param {React.ComponentProps<React.FunctionComponent<Omit<KPIChangeProps, "label">>>["before"]["value"]} value
   * @returns {React.ComponentProps<React.FunctionComponent<Omit<KPIChangeProps, "label">>>["before"]}
   */
  const getValueProps = (
    input: Result,
    variant: "before" | "after",
    value: ComponentProps<typeof KPIChange>["before" | "after"]["value"]
  ): ComponentProps<typeof KPIChange>["before" | "after"] => {
    /**
     * If the data is missing, find out why
     */
    const dataMissingReason = _.get(input, "dataUnavailabilityReason", null);

    return {
      value,
      noData: t<string>(noData.label, {
        verbose: false,
        dataStatus: input.dataAvailability,
        reason: dataMissingReason
      }),
      label: t<string>(variant === "before" ? before : after)
    };
  };

  /**
   * Make a label for missing data
   * @returns {any}
   */
  const getMissingDataLabel = (input?: Result) => {
    const isTracking = moment(startAt)
      .add(moment.duration(currentTimeSpan).asDays(), "days")
      .isAfter(moment(), "day");

    const { dataAvailability, dataUnavailabilityReason } = _.defaultTo(input, {
      dataAvailability: null,
      dataUnavailabilityReason: null
    });

    const reason = _.defaultTo(dataUnavailabilityReason, isTracking ? "tracking" : "empty");

    return t<string>(noData.label, {
      verbose: true,
      dataStatus: dataAvailability,
      reason
    });
  };

  return (
    <LayoutProvider farmID={farm?.id}>
      {() => (
        <DataDictionaryContext.Provider>
          <DataDictionaryContext.Consumer>
            {({ dataDescriptors: dataDictionary, meta: { isLoading: isDataDictionaryLoading } }) => (
              <ImpactTrackingContext.Provider annotationID={id} timeSpan={currentTimeSpan}>
                <ImpactTrackingContext.Consumer>
                  {({
                    results,
                    otherResults,
                    meta: {
                      isLoading: isImpactTrackingResultLoading,
                      isRejected,
                      isProcessing,
                      isAdding,
                      isIntervalSupported
                    }
                  }) => (
                    <section className={styles.PageWrapper}>
                      <Skeleton
                        active
                        loading={isDataDictionaryLoading || (isImpactTrackingResultLoading && !isProcessing)}
                      >
                        <Title level={6} theme="black">
                          <Space>
                            <span>{t<string>(title, { type })}</span>
                            {/* @todo make a component and remove when not needed anymore */}
                            <Tag
                              style={{
                                backgroundColor: "#666",
                                color: "#80FFEC",
                                border: "none",
                                position: "relative",
                                top: "-3px"
                              }}
                            >
                              {t<string>(beta)}
                            </Tag>
                          </Space>
                        </Title>
                        <Paragraph>{t<string>(description, { type })}</Paragraph>
                        <DateRangePicker
                          className={styles.RangePicker}
                          durations={_.map(currentDurations, (duration) => ({
                            duration,
                            label: t(timePeriod(duration))
                          }))}
                          value={currentTimeSpan}
                          center={moment(startAt)}
                          labels={{ before: t(before), after: t(after) }}
                          onChange={(value) => {
                            trackEvent(GAActions.pickTimePeriod, { label: value });
                            setCurrentTimeSpan(value);
                          }}
                        />
                        <OverlapEvent duration={currentTimeSpan} />
                        {isRejected ? (
                          <Alert
                            message={getMissingDataLabel()}
                            description={t(unsupportedIntervalDescription)}
                          />
                        ) : _.isEmpty(results) ? (
                          <Alert
                            message={t(noData.message, { isIntervalSupported })}
                            description={t(noData.description, { isIntervalSupported })}
                          />
                        ) : (
                          <>
                            <Row gutter={[16, 16]}>
                              {_.map(results, (result, dataDescriptorID) => {
                                /**
                                 * Get current change value based on current unit system
                                 */
                                const currentChange = result[unitSystem];

                                /**
                                 * Get data descriptor from data dictionary
                                 * @type {DataDescriptorEntity}
                                 */
                                const dataDescriptor = _.get(dataDictionary, [dataDescriptorID]);

                                /**
                                 * Get before and after values
                                 * @type {number}
                                 */
                                const beforeValue = _.round(
                                  currentChange?.before,
                                  _.get(dataDescriptor, ["valueProperties", "decimals"], 2)
                                );

                                const afterValue = _.round(
                                  currentChange?.after,
                                  _.get(dataDescriptor, ["valueProperties", "decimals"], 2)
                                );

                                /**
                                 * Get direction based on current change
                                 * @type {Direction.increase | Direction.stagnate | Direction.decrease}
                                 */
                                const direction =
                                  currentChange?.after && afterValue - beforeValue > 0
                                    ? Direction.increase
                                    : afterValue - beforeValue === 0
                                    ? Direction.stagnate
                                    : Direction.decrease;

                                isKPIProcessing.current = _.isEqual(
                                  _.get(currentChange, "dataAvailability"),
                                  DataAvailability.processing
                                );

                                /**
                                 * Get the units to use
                                 * @type {undefined}
                                 */
                                const units = _.get(dataDescriptor, ["valueProperties", "units"]);

                                return (
                                  <Col md={12} span={24} key={dataDescriptorID}>
                                    <KPIChange
                                      loading={!dataDescriptor || isKPIProcessing.current}
                                      labels={{
                                        kpi: dataDescriptor
                                          ? t(
                                              kpi.displayName(
                                                _.get(dataDescriptor, ["dataProperties", "typeName"])
                                              ),
                                              { variant: null, makeDefaultValue: true }
                                            )
                                          : "...",
                                        direction: t(change(direction)),
                                        noData: getMissingDataLabel(currentChange)
                                      }}
                                      before={getValueProps(
                                        currentChange,
                                        "before",
                                        _.isNull(currentChange?.before) ? null : beforeValue
                                      )}
                                      after={getValueProps(
                                        currentChange,
                                        "after",
                                        _.isNull(currentChange?.after) ? null : afterValue
                                      )}
                                      direction={direction}
                                      suffix={
                                        dataDescriptor
                                          ? t(unit(_.get(units, unitSystem, _.get(units, UnitSystem.metric))))
                                          : "..."
                                      }
                                      context={{
                                        dataStatus: _.defaultTo(currentChange?.dataAvailability, null),
                                        missingDataReason: _.defaultTo(
                                          currentChange?.dataUnavailabilityReason,
                                          null
                                        )
                                      }}
                                    />
                                  </Col>
                                );
                              })}
                              {(isProcessing || isAdding) && !isKPIProcessing.current && (
                                <Col md={12} span={24}>
                                  <Spin tip={t(add.alert.loading)}>
                                    <Alert
                                      message={t(add.alert.message)}
                                      description={t(add.alert.description)}
                                      type="info"
                                    />
                                  </Spin>
                                </Col>
                              )}
                              <Col md={12} span={24}>
                                <WidgetWrapper label={t(add.title)}>
                                  <Row gutter={[12, 0]} wrap={false}>
                                    <Col flex={1}>
                                      {/** @todo replace with metric picker when analytics v3 is done */}
                                      <ChartPicker
                                        showButton={false}
                                        showLabel={false}
                                        onChange={setNewMetric}
                                        filterValues={_.keys(results)}
                                      />
                                    </Col>
                                    <Col>
                                      <Button
                                        disabled={!newMetric}
                                        type="secondary"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                          trackEvent(GAActions.addNewMetric, { label: newMetric });

                                          dispatch(
                                            ImpactTrackingAPI.actions.addMetricToEvent.start(id, newMetric)
                                          );
                                        }}
                                      >
                                        {t(add.cta)}
                                      </Button>
                                    </Col>
                                  </Row>
                                </WidgetWrapper>
                              </Col>
                            </Row>
                            {!_.isEmpty(otherResults) && (
                              <>
                                <Title className={styles.Title} level={6} theme="black">
                                  {t(whatElse.title)}
                                </Title>
                                <Paragraph>{t(whatElse.description)}</Paragraph>
                                <WidgetWrapper
                                  className={styles.Table}
                                  label={
                                    <Row wrap={false}>
                                      <Col span={6}>
                                        <Text size="md">{t(kpiCol)}</Text>
                                      </Col>
                                      <Col span={6}>
                                        <Text size="md">{t(before)}</Text>
                                      </Col>
                                      <Col span={6}>
                                        <Text size="md">{t(after)}</Text>
                                      </Col>
                                      <Col span={6}>
                                        <Text size="md">{`${t(change(Direction.increase))} / ${t(
                                          change(Direction.decrease)
                                        )}`}</Text>
                                      </Col>
                                    </Row>
                                  }
                                >
                                  {_.map(otherResults, (result, dataDescriptorID) => {
                                    /**
                                     * Get current change value based on current unit system
                                     */
                                    const currentChange = result[unitSystem];

                                    /**
                                     * Get data descriptor from data dictionary
                                     * @type {DataDescriptorEntity}
                                     */
                                    const dataDescriptor = dataDictionary[dataDescriptorID];

                                    /**
                                     * Get before and after values
                                     * @type {number}
                                     */
                                    const beforeValue = _.round(
                                      currentChange?.before,
                                      _.get(dataDescriptor, ["valueProperties", "decimals"], 2)
                                    );

                                    const afterValue = _.round(
                                      currentChange?.after,
                                      _.get(dataDescriptor, ["valueProperties", "decimals"], 2)
                                    );

                                    /**
                                     * Get direction based on current change
                                     * @type {Direction.increase | Direction.stagnate | Direction.decrease}
                                     */
                                    const direction =
                                      currentChange?.after && afterValue - beforeValue > 0
                                        ? Direction.increase
                                        : afterValue - beforeValue === 0
                                        ? Direction.stagnate
                                        : Direction.decrease;

                                    const units = _.get(dataDescriptor, ["valueProperties", "units"]);

                                    return (
                                      <KPIChange
                                        key={dataDescriptorID}
                                        className={styles.TableEntry}
                                        variant={Variant.table}
                                        labels={{
                                          kpi: dataDescriptor
                                            ? t(
                                                kpi.displayName(
                                                  _.get(dataDescriptor, ["dataProperties", "typeName"])
                                                ),
                                                { variant: null, makeDefaultValue: true }
                                              )
                                            : "...",
                                          direction: t(change(direction))
                                        }}
                                        before={{
                                          value: beforeValue
                                        }}
                                        after={{
                                          value: afterValue
                                        }}
                                        direction={direction}
                                        suffix={
                                          dataDescriptor
                                            ? t(
                                                unit(
                                                  _.get(units, unitSystem, _.get(units, UnitSystem.metric))
                                                )
                                              )
                                            : "..."
                                        }
                                      />
                                    );
                                  })}
                                </WidgetWrapper>
                              </>
                            )}
                          </>
                        )}
                      </Skeleton>
                    </section>
                  )}
                </ImpactTrackingContext.Consumer>
              </ImpactTrackingContext.Provider>
            )}
          </DataDictionaryContext.Consumer>
        </DataDictionaryContext.Provider>
      )}
    </LayoutProvider>
  );
};
